import { useState, useMemo } from 'react'
import { ReportSortDir } from '../contexts/ReportsContextProvider'

export type SortState<T> = {
  sortBy: keyof T
  direction: ReportSortDir
}

type UseSortedTableParams<T> = {
  items: T[]
  initialSort: SortState<T>
  getSortableValue: (item: T, sortBy: keyof T) => string | number | boolean
}

export function useSortedTable<T>({ items, initialSort, getSortableValue }: UseSortedTableParams<T>) {
  const [sortState, setSortState] = useState<SortState<T>>(initialSort)

  const sortedItems = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const valA = getSortableValue(a, sortState.sortBy)
      const valB = getSortableValue(b, sortState.sortBy)
      const dir = sortState.direction === ReportSortDir.ASC ? 1 : -1

      if (valA < valB) return -1 * dir
      if (valA > valB) return 1 * dir
      return 0
    })
    return sorted
  }, [items, sortState, getSortableValue])

  const toggleSort = (column: keyof T, direction?: ReportSortDir) => {
    setSortState((prev) => {
      if (column === prev.sortBy && !direction) {
        return {
          sortBy: column,
          direction: prev.direction === ReportSortDir.ASC ? ReportSortDir.DESC : ReportSortDir.ASC,
        }
      }
      return {
        sortBy: column,
        direction: direction || ReportSortDir.ASC,
      }
    })
  }

  return {
    sortedItems,
    sortState,
    setSort: toggleSort,
  }
}
